import React, {useState} from "react"
import { Form, Input, Select, Divider, Switch } from "antd"
import _ from "lodash"
import { Row, Col } from "reactstrap"

interface PricingSectionProps {
  title: string
  prefix?: string
  audioSyncSwitch?: boolean
  handleInputChange: (event: React.KeyboardEvent<HTMLInputElement>) => void
  hasPricingPermissions: boolean
  form: ReturnType<typeof Form.useForm>[0]
  durations?: boolean
  mobile_margin?: boolean
  isMandatory?: boolean
}

const PricingSection: React.FC<PricingSectionProps> = ({
  title,
  prefix = "",
  handleInputChange,
  hasPricingPermissions,
  audioSyncSwitch = false,
  form,
  durations = true,
  mobile_margin = true,
  isMandatory = true,
}) => {
  const getFieldName = (base: string) => (prefix ? `${prefix}_${base}` : base)


  const syncPrices = (priceMultiplier: number) => {

    const audioValues = form.getFieldsValue([
      "audio_call_multiplier",
      "audio_call_start_with_min_balance",
      "audio_call_price",
      "audio_call_v_margin_pct",
      "audio_call_discount_price",
      "audio_call_extend_duration_1",
      "audio_call_extend_duration_1_list_price",
      "audio_call_extend_duration_1_discounted_price",
      "audio_call_extend_duration_2",
      "audio_call_extend_duration_2_list_price",
      "audio_call_extend_duration_2_discounted_price",
      "audio_call_extend_duration_3",
      "audio_call_extend_duration_3_list_price",
      "audio_call_extend_duration_3_discounted_price",
      "audio_call_android_v_margin_pct",
      "audio_call_ios_v_margin_pct",
    ])

    form.setFieldsValue({
      [getFieldName("multiplier")]: audioValues.audio_call_multiplier,
      [getFieldName("start_with_min_balance")]:
        audioValues.audio_call_start_with_min_balance,
      [getFieldName("price")]: Math.ceil(audioValues.audio_call_price * priceMultiplier),
      [getFieldName("v_margin_pct")]: audioValues.audio_call_v_margin_pct,
      [getFieldName("discount_price")]: Math.ceil(audioValues.audio_call_discount_price * priceMultiplier),
      [getFieldName("extend_duration_1")]:
        audioValues.audio_call_extend_duration_1,
      [getFieldName("extend_duration_1_list_price")]:
        Math.ceil(audioValues.audio_call_extend_duration_1_list_price * priceMultiplier),
      [getFieldName("extend_duration_1_discounted_price")]:
        Math.ceil(audioValues.audio_call_extend_duration_1_discounted_price * priceMultiplier),
      [getFieldName("extend_duration_2")]:
        audioValues.audio_call_extend_duration_2,
      [getFieldName("extend_duration_2_list_price")]:
        Math.ceil(audioValues.audio_call_extend_duration_2_list_price * priceMultiplier),
      [getFieldName("extend_duration_2_discounted_price")]:
        Math.ceil(audioValues.audio_call_extend_duration_2_discounted_price * priceMultiplier),
      [getFieldName("extend_duration_3")]:
        audioValues.audio_call_extend_duration_3,
      [getFieldName("extend_duration_3_list_price")]:
        Math.ceil(audioValues.audio_call_extend_duration_3_list_price * priceMultiplier),
      [getFieldName("extend_duration_3_discounted_price")]:
        Math.ceil(audioValues.audio_call_extend_duration_3_discounted_price * priceMultiplier),
      [getFieldName("android_v_margin_pct")]:
        audioValues.audio_call_android_v_margin_pct,
      [getFieldName("ios_v_margin_pct")]:
        audioValues.audio_call_ios_v_margin_pct,
    })
  }

  const selectOptions = [
    {"value": 0.3333,"label": "1/3 rd of Audio Call"},
    {"value": 0.50,"label": "1/2 rd of Audio Call"},
    {"value": 1,"label": "Same as Audio Call"},
    {"value": 1.5,"label": "1.5x of Audio Call"},
    {"value": 2,"label": "2x of Audio Call"}
  ]

  return (
    <React.Fragment>
      <Divider style={{ borderColor: "#bbb" }}>{title}</Divider>
      {audioSyncSwitch && (
            <Row>
              <Col lg="4">
                <Form.Item
                  label="Call Multiplier"
                  name={getFieldName("Call_Multiplier")}
                >
                  <Select
                      style={{width: "100%"}}
                      size="large"
                      options={selectOptions}
                      disabled={!hasPricingPermissions}
                      onChange={syncPrices}
                    />
                </Form.Item>
              </Col>
            </Row> 
      )}

      <Row>
        <Col lg={4}>
          <Form.Item
            label="Multiplier"
            name={getFieldName("multiplier")}
            initialValue={1}
          >
            <Input
              type="number"
              size="large"
              min={1}
              disabled={!hasPricingPermissions}
              onKeyDown={handleInputChange}
            />
          </Form.Item>
        </Col>
        <Col lg={4}>
          <Form.Item
            label="Start With Min Balance"
            name={getFieldName("start_with_min_balance")}
            valuePropName="checked"
          >
            <Switch defaultChecked={false} disabled={!hasPricingPermissions} />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col lg={4}>
          <Form.Item
            label={
              prefix === "audio" || prefix === ""
                ? "List Price/min (Jems)"
                : "Price"
            }
            name={getFieldName("price")}
            rules={
              isMandatory
                ? [{ required: true, message: "List Price/min is required" }]
                : []
            }
          >
            <Input
              type="number"
              size="large"
              min={1}
              onKeyDown={handleInputChange}
              disabled={!hasPricingPermissions}
            />
          </Form.Item>
        </Col>
        <Col lg={4}>
          <Form.Item
            label="Verse Margin (In Percentage)"
            name={getFieldName("v_margin_pct")}
            rules={
              isMandatory
                ? [
                    {
                      required: true,
                      message: "Verse Margin Percentage is required",
                    },
                  ]
                : []
            }
          >
            <Input
              type="number"
              size="large"
              min={1}
              onKeyDown={handleInputChange}
              disabled={!hasPricingPermissions}
            />
          </Form.Item>
        </Col>
        <Col lg={4}>
          <Form.Item
            label="Discounted price (Jems)"
            name={getFieldName("discount_price")}
            rules={
              isMandatory
                ? [{ required: true, message: "Discounted price is required" }]
                : []
            }
          >
            <Input
              type="number"
              size="large"
              min={1}
              onKeyDown={handleInputChange}
              disabled={!hasPricingPermissions}
            />
          </Form.Item>
        </Col>
      </Row>
      {durations && (
        <>
          {/* Extend Duration 1 */}
          <Row>
            <Col lg={4}>
              <Form.Item
                label="Extend Duration 1"
                name={getFieldName("extend_duration_1")}
              >
                <Select
                  size="large"
                  options={[
                    { label: "5 min", value: 5 },
                    { label: "10 min", value: 10 },
                    { label: "15 min", value: 15 },
                    { label: "30 min", value: 30 },
                  ]}
                  disabled={!hasPricingPermissions}
                />
              </Form.Item>
            </Col>
            <Col lg={4}>
              <Form.Item
                label="List price (Jems)"
                name={getFieldName("extend_duration_1_list_price")}
              >
                <Input
                  type="number"
                  size="large"
                  min={1}
                  onKeyDown={handleInputChange}
                  disabled={!hasPricingPermissions}
                />
              </Form.Item>
            </Col>
            <Col lg={4}>
              <Form.Item
                label="Discounted price (Jems)"
                name={getFieldName("extend_duration_1_discounted_price")}
              >
                <Input
                  type="number"
                  size="large"
                  min={1}
                  onKeyDown={handleInputChange}
                  disabled={!hasPricingPermissions}
                />
              </Form.Item>
            </Col>
          </Row>
          {/* Extend Duration 2 */}
          <Row>
            <Col lg={4}>
              <Form.Item
                label="Extend Duration 2"
                name={getFieldName("extend_duration_2")}
              >
                <Select
                  size="large"
                  options={[
                    { label: "5 min", value: 5 },
                    { label: "10 min", value: 10 },
                    { label: "15 min", value: 15 },
                    { label: "30 min", value: 30 },
                  ]}
                  disabled={!hasPricingPermissions}
                />
              </Form.Item>
            </Col>
            <Col lg={4}>
              <Form.Item
                label="List price (Jems)"
                name={getFieldName("extend_duration_2_list_price")}
              >
                <Input
                  type="number"
                  size="large"
                  min={1}
                  onKeyDown={handleInputChange}
                  disabled={!hasPricingPermissions}
                />
              </Form.Item>
            </Col>
            <Col lg={4}>
              <Form.Item
                label="Discounted price (Jems)"
                name={getFieldName("extend_duration_2_discounted_price")}
              >
                <Input
                  type="number"
                  size="large"
                  min={1}
                  onKeyDown={handleInputChange}
                  disabled={!hasPricingPermissions}
                />
              </Form.Item>
            </Col>
          </Row>
          {/* Extend Duration 3 */}
          <Row>
            <Col lg={4}>
              <Form.Item
                label="Extend Duration 3"
                name={getFieldName("extend_duration_3")}
              >
                <Select
                  size="large"
                  options={[
                    { label: "5 min", value: 5 },
                    { label: "10 min", value: 10 },
                    { label: "15 min", value: 15 },
                    { label: "30 min", value: 30 },
                  ]}
                  disabled={!hasPricingPermissions}
                />
              </Form.Item>
            </Col>
            <Col lg={4}>
              <Form.Item
                label="List price (Jems)"
                name={getFieldName("extend_duration_3_list_price")}
              >
                <Input
                  type="number"
                  size="large"
                  min={1}
                  onKeyDown={handleInputChange}
                  disabled={!hasPricingPermissions}
                />
              </Form.Item>
            </Col>
            <Col lg={4}>
              <Form.Item
                label="Discounted price (Jems)"
                name={getFieldName("extend_duration_3_discounted_price")}
              >
                <Input
                  type="number"
                  size="large"
                  min={1}
                  onKeyDown={handleInputChange}
                  disabled={!hasPricingPermissions}
                />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
      {/* Margin Fields */}
      {mobile_margin && (
        <Row>
          <Col lg={4}>
            <Form.Item
              label="Android Verse Margin (In Percentage)"
              name={getFieldName("android_v_margin_pct")}
            >
              <Input
                type="number"
                size="large"
                min={1}
                max={100}
                onKeyDown={handleInputChange}
                disabled={!hasPricingPermissions}
              />
            </Form.Item>
          </Col>
          <Col lg={4}>
            <Form.Item
              label="IOS Verse Margin (In Percentage)"
              name={getFieldName("ios_v_margin_pct")}
            >
              <Input
                type="number"
                size="large"
                min={1}
                max={100}
                onKeyDown={handleInputChange}
                disabled={!hasPricingPermissions}
              />
            </Form.Item>
          </Col>
        </Row>
      )}
    </React.Fragment>
  )
}

export default PricingSection
